import React, { Suspense } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ProjectList, mediaLookup } from '../helpers/ProjectList';
import MediaGallery from '../components/MediaGallery';
import EzRphVlog from '../components/EzRphVlog';
import "../styles/ProjectDisplay.css"
import { motion } from 'framer-motion';
import { FaArrowLeft, FaExternalLinkAlt, FaCalendarAlt, FaGithub } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

// three.js viewer loads in its own chunk, only for projects that declare 3D parts
const Hardware3DViewer = React.lazy(() => import('../components/Hardware3DViewer'));

function ProjectDisplay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].projectDisplay;
  const project = ProjectList.find((p) => p.projectId === Number(id));

  // Look up media assets for this project
  const assets = project ? (mediaLookup[project.projectId] || []) : [];
  const isVlog = project && project.template === 'vlog';

  if (!project) {
    return (
      <div className='project-display'>
        <div className='project-container'>
          <h1>{t.notFound}</h1>
          <button className='back-btn' onClick={() => navigate('/projects')}>
            <FaArrowLeft /> {t.back}
          </button>
        </div>
      </div>
    );
  }

  const isGithubLink = project.link && project.link.includes('github.com');

  return (
    <div className='project-display'>
      <motion.div
        className='project-container'
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <button className='back-btn' onClick={() => navigate('/projects')}>
          <FaArrowLeft /> {t.back}
        </button>

        <div className='project-header'>
          <h1 className='project-title'>{project.name}</h1>
          {project.tagline && <p className='project-tagline'>{project.tagline}</p>}
          <div className='project-meta'>
            <span className='project-year'>
              <FaCalendarAlt /> {project.year}
            </span>
            {project.status && (
              <span className='project-status-pill'>{project.status}</span>
            )}
            {project.link && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className='project-link'
              >
                {isGithubLink ? <FaGithub /> : <FaExternalLinkAlt />}
                {' '}{isGithubLink ? t.sourceCode : t.visitProduct}
              </a>
            )}
          </div>
          {project.technologies && project.technologies.length > 0 && (
            <div className='project-tech-list'>
              {project.technologies.map((tech) => (
                <Link
                  key={tech}
                  className='tech-chip tech-chip-link'
                  to={`/skills?q=${encodeURIComponent(tech)}`}
                  title={t.techChipHint}
                >
                  {tech}
                </Link>
              ))}
            </div>
          )}
        </div>

        {project.statusNote && (
          <div className='project-status-note'>
            <strong>{project.status}</strong>
            <span>{project.statusNote}</span>
          </div>
        )}

        {/* Ez-RPH renders as a chaptered vlog walkthrough */}
        {isVlog ? (
          <EzRphVlog />
        ) : assets.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <MediaGallery assets={assets} />
          </motion.div>
        ) : project.image ? (
          <motion.div
            className='project-image-container'
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <img src={project.image} alt={project.name} />
          </motion.div>
        ) : null}

        {project.viewer3d && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <h2 className='section-label'>{t.viewer3dTitle}</h2>
            <Suspense fallback={<div className='hw3d-fallback'>{t.viewer3dLoading}</div>}>
              <Hardware3DViewer
                // key remounts the viewer when navigating between 3D projects
                key={project.projectId}
                parts={project.viewer3d.parts}
                defaultOpacity={project.viewer3d.defaultOpacity}
                labels={{
                  loading: t.viewer3dLoading,
                  hint: t.viewer3dHint,
                  opacity: t.viewer3dOpacity,
                  fallback: t.viewer3dFallback,
                  ariaLabel: `${t.viewer3dTitle} — ${project.name}`,
                  parts: {
                    pcb: t.viewer3dPartPcb,
                    body: t.viewer3dPartBody,
                    cover: t.viewer3dPartCover,
                    plates: t.viewer3dPartPlates,
                    frame: t.viewer3dPartFrame,
                    housing: t.viewer3dPartHousing,
                  },
                }}
              />
            </Suspense>
          </motion.div>
        )}

        <motion.div
          className='project-content'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h2 className='section-label'>{t.about}</h2>
          <p className='project-description'>{project.description}</p>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default ProjectDisplay
